import { useFormContext, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { PromptEditor } from '../prompt-editor'
import { SYSTEM_PROMPT_TEMPLATES } from '../prompt-templates'
import type { AgentWizardData } from '@/schemas/agent-wizard'
import { FileText, Wand2, CheckCircle2 } from 'lucide-react'
import { useState } from 'react'
import { cn } from '@/lib/utils'
import { useAgentWizardStore } from '@/stores/agent-wizard-store'

const TEMPLATES = [
  {
    id: 'assistente',
    icon: '🤖',
  },
  {
    id: 'vendedor',
    icon: '💼',
  },
  {
    id: 'suporte',
    icon: '🛠️',
  },
  {
    id: 'analista',
    icon: '📊',
  },
] as const

type TemplateId = (typeof TEMPLATES)[number]['id']

export function StepSystemPrompt() {
  const { t } = useTranslation('agents')
  const { control, setValue, watch, formState: { errors } } = useFormContext<AgentWizardData>()
  const [localSelectedTemplate, setLocalSelectedTemplate] = useState<TemplateId | null>(null)
  const { selectedTemplate: appliedAgentTemplate } = useAgentWizardStore()

  const currentPrompt = watch('system_prompt')

  const handleTemplateSelect = (templateId: TemplateId) => {
    setLocalSelectedTemplate(templateId)
    const template = SYSTEM_PROMPT_TEMPLATES[templateId]
    setValue('system_prompt', template)
  }

  const characterCount = currentPrompt?.length ?? 0
  const maxCharacters = 100000

  return (
    <div className="space-y-6">
      {/* Template Selection - hidden if Agent Template was applied */}
      {!appliedAgentTemplate ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-gray-300 flex items-center gap-2">
              <Wand2 className="h-4 w-4" />
              {t('wizard.systemPrompt.templates')}
            </Label>
            <span className="text-xs text-gray-500">{t('wizard.systemPrompt.templatesTip')}</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {TEMPLATES.map((template) => (
              <button
                key={template.id}
                type="button"
                onClick={() => handleTemplateSelect(template.id)}
                className={cn(
                  'p-3 rounded-xl text-left transition-all duration-200',
                  'bg-white/5 border border-gray-700/50',
                  'hover:bg-white/10 hover:border-gray-600',
                  localSelectedTemplate === template.id &&
                    'bg-indigo-500/20 border-indigo-500/50'
                )}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xl">{template.icon}</span>
                  <span className="text-sm font-medium text-white">
                    {t(`wizard.systemPrompt.promptTemplates.${template.id}`)}
                  </span>
                </div>
                <p className="text-xs text-gray-500">{t(`wizard.systemPrompt.promptTemplates.${template.id}Desc`)}</p>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="p-3 rounded-xl bg-indigo-500/10 border border-indigo-500/30">
          <p className="text-sm text-indigo-300 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4" />
            {t('wizard.systemPrompt.templateApplied', { name: appliedAgentTemplate.name })}
          </p>
          <p className="text-xs text-gray-500 mt-1 ml-6">
            {t('wizard.systemPrompt.editBelow')}
          </p>
        </div>
      )}

      {/* System Prompt Editor */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-gray-300 flex items-center gap-2">
            <FileText className="h-4 w-4" />
            {t('wizard.systemPrompt.prompt')}
          </Label>
          <span
            className={cn(
              'text-xs',
              characterCount > maxCharacters * 0.9
                ? 'text-amber-400'
                : 'text-gray-500'
            )}
          >
            {characterCount.toLocaleString()} / {maxCharacters.toLocaleString()}
          </span>
        </div>

        <Controller
          name="system_prompt"
          control={control}
          render={({ field }) => (
            <PromptEditor
              value={field.value ?? ''}
              onChange={field.onChange}
              placeholder={t('wizard.systemPrompt.promptPlaceholder')}
              minHeight="300px"
              maxHeight="500px"
            />
          )}
        />
        {errors.system_prompt && (
          <p className="text-sm text-red-400">{errors.system_prompt.message}</p>
        )}
      </div>

      {/* Tips */}
      <div className="p-4 rounded-xl bg-white/5 border border-gray-700/50">
        <p className="text-sm font-medium text-gray-300 mb-2">
          {t('wizard.systemPrompt.tips')}
        </p>
        <ul className="text-xs text-gray-500 space-y-1 list-disc list-inside">
          <li>{t('wizard.systemPrompt.tip1')}</li>
          <li>{t('wizard.systemPrompt.tip2')}</li>
          <li>{t('wizard.systemPrompt.tip3')}</li>
          <li>{t('wizard.systemPrompt.tip4')}</li>
          <li>{t('wizard.systemPrompt.tip5')}</li>
        </ul>
      </div>

      {/* Clear Button */}
      {currentPrompt && (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setValue('system_prompt', null)
              setLocalSelectedTemplate(null)
            }}
            className="text-gray-400 hover:text-white"
          >
            {t('wizard.systemPrompt.clearPrompt')}
          </Button>
        </div>
      )}
    </div>
  )
}
